import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  REVIEWS_PER_PAGE,
  Review,
  clearReviewsCache,
  deleteReview,
  getReviews,
  updateReviewResponse,
  updateReviewVisibility,
  updateReviewWording,
  updateTitleWording,
} from '~/services/ReviewService';

export interface TourReviewFilters {
  page: number;
  showHidden: boolean;
  showLeReviews: boolean;
  showExternalReviews: boolean;
}

export interface ReviewChange {
  isPubliclyVisible?: boolean;
  response?: string;
  title?: string;
  review?: string;
}

export const tourReviewQueryKeys = {
  all: ['tour-reviews'] as const,
  lists: (tourId: string) => [...tourReviewQueryKeys.all, tourId] as const,
  list: (tourId: string, filters: TourReviewFilters) => [...tourReviewQueryKeys.lists(tourId), filters] as const,
};

/** A page of a tour's reviews, its shared tours' included. */
export function useGetTourReviews(tourId: string, filters: TourReviewFilters) {
  return useQuery({
    queryKey: tourReviewQueryKeys.list(tourId, filters),
    queryFn: async (): Promise<{ reviews: Array<Review>; total: number }> => {
      const { result, total } = await getReviews({
        tourId,
        offset: (filters.page - 1) * REVIEWS_PER_PAGE,
        limit: REVIEWS_PER_PAGE,
        showHidden: filters.showHidden,
        useLeReviews: filters.showLeReviews,
        useBedbankReviews: false,
        useExternalReviews: filters.showExternalReviews,
      });
      // External (Google) reviews have no id of their own.
      const reviews = (result ?? []).map((review: Review, index: number) => ({
        ...review,
        id: review.source === 'google' ? `${review.createdAt}-${index}` : review.id,
      }));
      return { reviews, total: total ?? 0 };
    },
    enabled: !!tourId,
    placeholderData: keepPreviousData,
  });
}

/** Each change is its own svc-review call, in the order the old review dialog made them. */
export function useUpdateTourReview(tourId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reviewId, change }: { reviewId: string; change: ReviewChange }) => {
      if (change.isPubliclyVisible !== undefined) {
        await updateReviewVisibility(reviewId, { isPubliclyVisible: change.isPubliclyVisible });
      }
      if (change.response !== undefined) {
        await updateReviewResponse(reviewId, change.response);
      }
      if (change.review !== undefined) {
        await updateReviewWording(reviewId, change.review);
      }
      if (change.title !== undefined) {
        await updateTitleWording(reviewId, change.title);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: tourReviewQueryKeys.lists(tourId) }),
  });
}

export function useDeleteTourReview(tourId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reviewId: string) => deleteReview(reviewId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: tourReviewQueryKeys.lists(tourId) }),
  });
}

/** svc-review works the rating out again the next time a customer sees the tour. */
export function useClearTourReviewRating(tourId: string) {
  return useMutation({ mutationFn: () => clearReviewsCache({ tourId }) });
}
